import { useEffect, useId, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Icon } from '../core/Icon';
import { Avatar } from '../core/Avatar';
import { Badge } from '../core/Badge';
import { useTopbarCompacta } from './topbarCompacta';

export type Presenca = 'online' | 'ausente' | 'pausado' | 'offline';
export type Tema = 'claro' | 'escuro' | 'sistema';

interface OpcaoPresenca {
    label: string;
    cor: string;
    icon: string;
}

const PRESENCA: Record<Presenca, OpcaoPresenca> = {
    online: { label: 'Disponível', cor: 'var(--success)', icon: 'circle' },
    ausente: { label: 'Ausente', cor: 'var(--warning)', icon: 'clock' },
    pausado: { label: 'Pausado', cor: 'var(--neutral-400)', icon: 'pause' },
    offline: { label: 'Offline', cor: 'var(--neutral-300)', icon: 'circle-off' },
};

/* Uma lista só para o rótulo do bloco e para as opções. Antes eram duas
   tabelas paralelas, e elas já divergiam: o bloco fechado dizia "Sistema" e a
   opção correspondente dizia "Automático". */
const TEMAS: Record<Tema, { label: string; icon: string }> = {
    claro: { label: 'Claro', icon: 'sun' },
    escuro: { label: 'Escuro', icon: 'moon' },
    sistema: { label: 'Automático', icon: 'monitor' },
};

const ORDEM_DOS_TEMAS: Tema[] = ['claro', 'escuro', 'sistema'];

export interface AccountMenuAccount {
    id: string;
    nome: string;
    papel?: string;
    atual?: boolean;
}

export interface AccountMenuEntradaExtra {
    id: string;
    label: string;
    icon?: string;
}

export interface AccountMenuProps {
    /** Nome exibido — é dele que saem as iniciais do avatar. */
    user: string;
    email?: string;
    /** Papel curto, ex.: "Administradora". */
    role?: string;
    avatarSrc?: string;
    /** Contas/clínicas para as quais a pessoa pode trocar. Omita para esconder o seletor. */
    contas?: AccountMenuAccount[];
    presenca?: Presenca;
    onPresencaChange?: (presenca: Presenca) => void;
    tema?: Tema;
    onTemaChange?: (tema: Tema) => void;
    /** Versão do app no rodapé do menu, ex.: "2.14.0". */
    versao?: string;
    onEditarConta?: () => void;
    onTrocarConta?: (conta: AccountMenuAccount) => void;
    onSair?: () => void;
    /** Entradas extras acima de "Sair" — o id delas chega por onSelect. */
    extra?: AccountMenuEntradaExtra[];
    /** Manipulador de reserva de toda entrada: recebe "editar", "seguranca", "notificacoes", "novidades", "sair", "presenca:<id>", "tema:<id>", "conta:<id>". */
    onSelect?: (id: string) => void;
    /**
     * Entradas que não se aplicam a esta conta. A de operador da plataforma não
     * tem status nem cadastro de clínica: oferecer os dois seria item que abre e
     * não faz nada.
     */
    ocultar?: ('presenca' | 'editar' | 'notificacoes')[];
    style?: CSSProperties;
}

type BlocoAberto = 'presenca' | 'tema' | 'contas' | null;

export function AccountMenu({
    user, email, role, avatarSrc, contas = [], presenca = 'online', onPresencaChange,
    tema = 'claro', onTemaChange, versao, onEditarConta, onTrocarConta, onSair, onSelect,
    extra = [], ocultar = [], style,
}: AccountMenuProps) {
    const semPresenca = ocultar.includes('presenca');
    const compacta = useTopbarCompacta();
    const [open, setOpen] = useState(false);
    const [sub, setSub] = useState<BlocoAberto>(null);
    const ref = useRef<HTMLDivElement | null>(null);
    const gatilhoRef = useRef<HTMLButtonElement | null>(null);
    const painelId = useId();

    useEffect(() => {
        if (!open) return;
        const fora = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setSub(null); }
        };
        const esc = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            setOpen(false);
            setSub(null);
            /* Escape sem devolver o foco deixava quem navega por teclado no
               início do documento, longe de onde estava. */
            gatilhoRef.current?.focus();
        };
        document.addEventListener('mousedown', fora);
        document.addEventListener('keydown', esc);
        return () => { document.removeEventListener('mousedown', fora); document.removeEventListener('keydown', esc); };
    }, [open]);

    const p = PRESENCA[presenca] ?? PRESENCA.online;
    const temaAtual = TEMAS[tema] ?? TEMAS.claro;
    const fechar = () => { setOpen(false); setSub(null); };
    const alternarBloco = (id: Exclude<BlocoAberto, null>) => setSub((s) => (s === id ? null : id));

    return (
        <div ref={ref} style={{ position: 'relative', display: 'inline-flex', ...style }}>
            {/* Painel de conta, não menu: ele mistura identidade, blocos que
                abrem e fecham, ações e rodapé. `role="menu"` prometeria um
                conjunto plano de menuitems navegável por setas — que este
                componente não implementa — e tiraria o Tab, que aqui funciona.
                Fica o padrão de divulgação: aria-expanded + aria-controls. */}
            <button
                ref={gatilhoRef} type="button"
                aria-expanded={open} aria-controls={open ? painelId : undefined}
                aria-label={`Conta de ${user}${role ? ` — ${role}` : ''}${semPresenca ? '' : `. Status: ${p.label}`}`}
                onClick={() => setOpen((o) => !o)}
                title={user}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8, height: 36, padding: '0 4px',
                    border: `1px solid ${open ? 'var(--border-strong)' : 'transparent'}`,
                    background: open ? 'var(--surface-secondary)' : 'transparent',
                    borderRadius: 'var(--radius-pill)', cursor: 'pointer', transition: 'var(--transition-control)',
                }}
            >
                <span style={{ position: 'relative', display: 'inline-flex' }}>
                    <Avatar name={user} src={avatarSrc} size="sm" />
                    <span
                        aria-hidden="true" className={presenca === 'online' ? 'vec-live' : undefined}
                        style={{ position: 'absolute', right: -1, bottom: -1, width: 9, height: 9, borderRadius: '50%', background: p.cor, border: '2px solid var(--surface)' }}
                    />
                </span>
                {/* Na topbar compacta o avatar basta: a seta ocupava a largura
                    que falta ao caminho da página. */}
                {!compacta && <Icon name="chevron-down" size={13} style={{ color: 'var(--neutral-500)', marginRight: 3 }} />}
            </button>

            {open && (
                <div
                    id={painelId}
                    style={{
                        position: 'absolute', right: 0, top: 'calc(100% + 8px)', zIndex: 60, width: 268,
                        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
                        boxShadow: 'var(--shadow-lg)', padding: 6, animation: 'vec-acct var(--dur-base) var(--ease-entrance)',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 10px 11px' }}>
                        <span style={{ position: 'relative', display: 'inline-flex' }}>
                            <Avatar name={user} src={avatarSrc} size="md" />
                            <span aria-hidden="true" style={{ position: 'absolute', right: -1, bottom: -1, width: 11, height: 11, borderRadius: '50%', background: p.cor, border: '2px solid var(--surface)' }} />
                        </span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ display: 'block', fontFamily: 'var(--font-ui)', fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user}</span>
                            {email && <span style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</span>}
                        </span>
                        {role && <Badge tone="brand">{role}</Badge>}
                    </div>

                    <Sep />

                    {!semPresenca && (
                        <SubBloco
                            id={`${painelId}-presenca`} aberto={sub === 'presenca'} onToggle={() => alternarBloco('presenca')}
                            icon={p.icon} label="Status" valor={p.label} dot={p.cor}
                        >
                            {(Object.keys(PRESENCA) as Presenca[]).map((k) => {
                                const o = PRESENCA[k];
                                return (
                                    <MenuLinha
                                        key={k} label={o.label} dot={o.cor} selecionado={k === presenca} semantica="opcao"
                                        onClick={() => { onPresencaChange?.(k); onSelect?.(`presenca:${k}`); setSub(null); }}
                                    />
                                );
                            })}
                        </SubBloco>
                    )}

                    <SubBloco
                        id={`${painelId}-tema`} aberto={sub === 'tema'} onToggle={() => alternarBloco('tema')}
                        icon={temaAtual.icon} label="Aparência" valor={temaAtual.label}
                    >
                        {ORDEM_DOS_TEMAS.map((k) => (
                            <MenuLinha
                                key={k} icon={TEMAS[k].icon} label={TEMAS[k].label} selecionado={k === tema} semantica="opcao"
                                onClick={() => { onTemaChange?.(k); onSelect?.(`tema:${k}`); setSub(null); }}
                            />
                        ))}
                    </SubBloco>

                    <Sep />

                    {!ocultar.includes('editar') && (
                        <MenuLinha icon="user-cog" label="Editar conta" onClick={() => { fechar(); if (onEditarConta) onEditarConta(); else onSelect?.('editar'); }} />
                    )}
                    <MenuLinha icon="shield" label="Segurança e senha" onClick={() => { fechar(); onSelect?.('seguranca'); }} />
                    {!ocultar.includes('notificacoes') && (
                        <MenuLinha icon="bell" label="Preferências de notificação" onClick={() => { fechar(); onSelect?.('notificacoes'); }} />
                    )}

                    {contas.length > 0 && (
                        <>
                            <Sep />
                            <SubBloco
                                id={`${painelId}-contas`} aberto={sub === 'contas'} onToggle={() => alternarBloco('contas')}
                                icon="arrow-left-right" label="Trocar de conta" valor={`${contas.length} disponíveis`}
                            >
                                {contas.map((c) => (
                                    <MenuLinha
                                        key={c.id} label={c.nome} sublabel={c.papel} selecionado={!!c.atual} semantica="atual"
                                        onClick={() => { fechar(); if (onTrocarConta) onTrocarConta(c); else onSelect?.(`conta:${c.id}`); }}
                                    />
                                ))}
                            </SubBloco>
                        </>
                    )}

                    <Sep />

                    {extra.length > 0 && (
                        <>
                            {extra.map((e) => (
                                <MenuLinha key={e.id} icon={e.icon ?? 'circle'} label={e.label} onClick={() => { fechar(); onSelect?.(e.id); }} />
                            ))}
                            <Sep />
                        </>
                    )}

                    <MenuLinha icon="log-out" label="Sair" onClick={() => { fechar(); if (onSair) onSair(); else onSelect?.('sair'); }} />

                    {versao && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 10px 5px', borderTop: '1px solid var(--border-subtle)', marginTop: 5 }}>
                            <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>Versão {versao}</span>
                            <span style={{ marginLeft: 'auto' }} />
                            <button
                                type="button" onClick={() => { fechar(); onSelect?.('novidades'); }}
                                style={{ border: 0, background: 'transparent', cursor: 'pointer', fontFamily: 'var(--font-ui)', fontSize: 11.5, fontWeight: 600, color: 'var(--text-brand)', padding: 0 }}
                            >
                                Novidades
                            </button>
                        </div>
                    )}
                    <style>{'@keyframes vec-acct{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:none}}'}</style>
                </div>
            )}
        </div>
    );
}

function Sep() {
    return <span aria-hidden="true" style={{ display: 'block', height: 1, background: 'var(--border-subtle)', margin: '5px 0' }} />;
}

interface MenuLinhaProps {
    icon?: string;
    label: ReactNode;
    sublabel?: ReactNode;
    dot?: string;
    selecionado?: boolean;
    /**
     * Como o estado selecionado é anunciado. `opcao` é escolha dentro de um
     * conjunto exclusivo (status, aparência) e vira `aria-pressed`; `atual`
     * marca a conta em uso e vira `aria-current`. Sem isto a marca de seleção
     * existiria só no negrito e no ícone de check — invisível para quem ouve.
     */
    semantica?: 'opcao' | 'atual';
    onClick?: () => void;
}

function MenuLinha({ icon, label, sublabel, dot, selecionado, semantica, onClick }: MenuLinhaProps) {
    const [hover, setHover] = useState(false);
    return (
        <button
            type="button" onClick={onClick}
            aria-pressed={semantica === 'opcao' ? !!selecionado : undefined}
            aria-current={semantica === 'atual' && selecionado ? 'true' : undefined}
            onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
            style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 32, padding: '5px 10px',
                border: 0, borderRadius: 'var(--radius-xs)', cursor: 'pointer', textAlign: 'left',
                background: hover ? 'var(--surface-secondary)' : 'transparent',
                fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: selecionado ? 600 : 500,
                color: selecionado ? 'var(--text-brand)' : 'var(--neutral-700)',
                transition: 'var(--transition-control)',
            }}
        >
            {dot ? <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: dot, flex: '0 0 auto', marginLeft: 4, marginRight: 4 }} />
                : icon ? <Icon name={icon} size={15} style={{ flex: '0 0 auto', color: selecionado ? 'var(--text-brand)' : 'var(--neutral-600)' }} />
                    : <span style={{ width: 15, flex: '0 0 auto' }} />}
            <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                {sublabel && <span style={{ display: 'block', fontSize: 11.5, fontWeight: 400, color: 'var(--text-tertiary)' }}>{sublabel}</span>}
            </span>
            {selecionado && <Icon name="check" size={14} style={{ flex: '0 0 auto', color: 'var(--text-brand)' }} />}
        </button>
    );
}

interface SubBlocoProps {
    id: string;
    aberto: boolean;
    onToggle: () => void;
    icon: string;
    label: string;
    valor: ReactNode;
    dot?: string;
    children: ReactNode;
}

function SubBloco({ id, aberto, onToggle, icon, label, valor, dot, children }: SubBlocoProps) {
    const [hover, setHover] = useState(false);
    return (
        <div>
            <button
                type="button" aria-expanded={aberto} aria-controls={aberto ? id : undefined} onClick={onToggle}
                onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
                style={{
                    display: 'flex', alignItems: 'center', gap: 10, width: '100%', height: 32, padding: '0 10px',
                    border: 0, borderRadius: 'var(--radius-xs)', cursor: 'pointer', textAlign: 'left',
                    background: hover || aberto ? 'var(--surface-secondary)' : 'transparent',
                    fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 500, color: 'var(--neutral-700)',
                    transition: 'var(--transition-control)',
                }}
            >
                {dot ? <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: dot, flex: '0 0 auto', marginLeft: 4, marginRight: 4 }} />
                    : <Icon name={icon} size={15} style={{ flex: '0 0 auto', color: 'var(--neutral-600)' }} />}
                <span style={{ flex: 1 }}>{label}</span>
                <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{valor}</span>
                <Icon name={aberto ? 'chevron-up' : 'chevron-down'} size={13} style={{ flex: '0 0 auto', color: 'var(--neutral-500)' }} />
            </button>
            {aberto && (
                <div id={id} role="group" aria-label={label} style={{ padding: '3px 0 3px 10px', display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {children}
                </div>
            )}
        </div>
    );
}
